/**
 * Workflow Step List Component
 * Displays the list of workflow steps with their status and timing
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader } from 'lucide-react';
import { formatDurationMs } from '../../utils/formatters';
import type { WorkflowStep } from '../../hooks/useAnalysisJob';

interface WorkflowStepListProps {
  steps: WorkflowStep[];
}

// Only the running step needs a live duration. Owning the ticker here
// instead of in useAnalysisJob avoids re-rendering the full page subtree
// 10× per second; only this component (and its memoised children) tick.
function useLiveTimer(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const interval = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(interval);
  }, [active]);
  return now;
}

// Newest step on top. Backend can emit steps out of order, so sort by
// canonical `order` descending rather than blindly reversing; steps with
// no order sink to the bottom instead of floating up as if they were newest.
function orderWorkflowSteps(steps: WorkflowStep[]): WorkflowStep[] {
  return steps
    .map((step, index) => ({ step, index }))
    .sort((a, b) => {
      const orderDelta =
        (b.step.order ?? Number.MIN_SAFE_INTEGER) - (a.step.order ?? Number.MIN_SAFE_INTEGER);
      return orderDelta || b.index - a.index;
    })
    .map(({ step }) => step);
}

export const WorkflowStepList: React.FC<WorkflowStepListProps> = ({ steps }) => {
  const hasRunning = steps.some((step) => (step.status || 'pending') === 'running');
  const currentTime = useLiveTimer(hasRunning);
  const orderedSteps = useMemo(() => orderWorkflowSteps(steps), [steps]);

  return (
    <div className="space-y-3 mb-6">
      {orderedSteps.map((step) => {
        const stepStatus = step.status || 'pending';
        const isCompleted = stepStatus === 'completed' || stepStatus === 'success';
        const isRunning = stepStatus === 'running';
        const isFailed = stepStatus === 'failed' || stepStatus === 'error';
        const isPending = stepStatus === 'pending';

        let durationDisplay = '';
        if (isCompleted && step.durationSeconds !== null && step.durationSeconds !== undefined) {
          durationDisplay = formatDurationMs(step.durationSeconds * 1000);
        } else if (isRunning && step.startTime) {
          const startTimeMs = new Date(step.startTime).getTime();
          const elapsedMs = currentTime - startTimeMs;
          durationDisplay = formatDurationMs(elapsedMs);
        }

        return (
          <div
            key={step.id}
            className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/30 dark:bg-gray-800/30"
          >
            <div className="flex items-center">
              <div className="shrink-0 mr-3">
                {isCompleted && (
                  <div className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                )}
                {isRunning && <Loader className="w-5 h-5 text-teal-500 animate-spin" />}
                {isFailed && (
                  <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                )}
                {isPending && (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <h3
                    className={`font-semibold text-sm ${
                      isCompleted
                        ? 'text-gray-900 dark:text-white'
                        : isRunning
                          ? 'text-teal-900 dark:text-teal-100'
                          : isFailed
                            ? 'text-red-900 dark:text-red-200'
                            : 'text-gray-500 dark:text-gray-500'
                    }`}
                  >
                    {step.title}
                  </h3>
                  {durationDisplay && (
                    <span
                      className={`text-xs font-mono font-semibold px-2 py-0.5 rounded ${
                        isRunning
                          ? 'text-teal-700 dark:text-teal-300 bg-teal-100 dark:bg-teal-900/30'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {durationDisplay}
                    </span>
                  )}
                </div>
                <p
                  className={`text-xs mt-0.5 ${
                    isFailed
                      ? 'text-red-700 dark:text-red-300'
                      : isRunning
                        ? 'text-teal-600 dark:text-teal-400'
                        : 'text-gray-500 dark:text-gray-500'
                  }`}
                >
                  {typeof step.error === 'string' ? step.error : step.description}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
